import React, { useState, useEffect } from 'react';

import { FolderIcon, DotIcon } from '@vlabs/icons';
import { optionPropType } from '@vlabs/shared';
import {
  DateInputsPopover,
  SourceSelectPopover,
  HandlersSelectPopover,
  TagsSelectPopover,
  EventIdsInputsPopover,
  LabelInputPopover,
  SimilarityInputsPopover,
  TopSimilarObjectIdsInputPopover,
  LivenessCheckboxesPopover,
  DeepfakeCheckboxesPopover,
  EmotionsCheckboxesPopover,
  MasksCheckboxesPopover,
  AgeCheckboxesPopover,
  GenderCheckboxesPopover,
  FaceIdsInputPopover,
  ApparentAgeCheckboxesPopover,
  ApparentGenderCheckboxesPopover,
  LowerCheckboxesPopover,
  UpperCheckboxesPopover,
  HeadwearCheckboxesPopover,
  ShoesColorsCheckboxesPopover,
  BackpackCheckboxesPopover,
  GeolocationFilter,
  TrackIdsInputPopover,
  MetaPopover,
  ListsFilterWithLeftPopover,
} from '@vlabs/shared/components/filters-panel';
import {
  Control, Group, SideMenu, useWindowSize, Badge,
} from '@vlabs/uikit';
import PropTypes from 'prop-types';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';

import { selectHandlerOptions } from '@vlabs/pages/handlers/selectors';

import { defaultFilters } from '@vlabs/pages/events/defaultFilters';

import st from './FilterForm.module.sass';
import { setFilterCounts } from '../events/store';

function FilterForm({
  handlerOptions = [],
  hasMap = false,
  setTotalCount = undefined,
}) {
  const { t } = useTranslation();
  const {
    reset, watch, setValue, setFilters,
  } = useFormContext();
  const [counts, setCounts] = useState({});
  const handleCountChange = (key, count) => {
    setCounts((prev) => {
      if (prev[key] === count) return prev;
      return { ...prev, [key]: count };
    });
  };
  const sumCounts = (keys) => keys.reduce((acc, key) => acc + (counts[key] || 0), 0);

  const topMatchingCandidates = watch('top_matching_candidates');
  const listId = watch('list_id');

  useEffect(() => {
    // в апи уходит сборный top_matching_candidates_label
    // из поля top_matching_candidates (параметр сравнения) или list_id
    const value = topMatchingCandidates || listId || '';
    setValue('top_matching_candidates_label', value);
  }, [topMatchingCandidates, listId, setValue]);

  const filterCountCommon = sumCounts([
    'date', 'sources', 'stream_ids', 'handlers',
    'list_id', 'tags', 'events', 'location',
  ]);

  const filterCountComparison = sumCounts([
    'top_matching_candidates',
    'similarity',
    'top_similar_object_ids',
  ]);

  const filterCountFace = sumCounts([
    'gender', 'age', 'deepfake', 'liveness',
    'emotions', 'masks', 'face_ids',
  ]);

  const filterCountBody = sumCounts([
    'apparent_gender', 'apparent_age', 'lower',
    'upper', 'headwear', 'shoes', 'backpack',
  ]);

  const filterCountOther = sumCounts([
    'meta',
    'track_ids',
  ]);

  useEffect(() => {
    const totalCount = Object.values(counts).reduce((acc, n) => acc + (n || 0), 0);
    setTotalCount(totalCount);
  }, [counts, setTotalCount]);

  const resetForm = () => {
    reset(defaultFilters);
    setFilters({});
  };
  const windowSize = useWindowSize();
  const isCompact = windowSize.width < 1920;

  return (
    <div
      className={st.FilterForm}
      data-testid="filtersPanel"
    >
      <Group
        gapSize="0"
        vertical
      >
        <SideMenu.MenuItem
          data-testid="events.commonFilters"
          leftIcon={<FolderIcon />}
          rightIcon={filterCountCommon > 0 && <Badge value={filterCountCommon} />}
          value={t('general')}
        >
          <DateInputsPopover onCountChange={handleCountChange} />
          <SourceSelectPopover onCountChange={handleCountChange} />
          <HandlersSelectPopover
            handlerOptions={handlerOptions}
            onCountChange={handleCountChange}
          />
          <GeolocationFilter
            hasMap={hasMap}
            onCountChange={handleCountChange}
          />
          <ListsFilterWithLeftPopover
            badge={listId ? <DotIcon className={st.Badge} /> : undefined}
            disabled={!!topMatchingCandidates}
            popoverProps={{ onCountChange: handleCountChange }}
            value={t('list')}
          />
          <TagsSelectPopover onCountChange={handleCountChange} />
          <EventIdsInputsPopover onCountChange={handleCountChange} />
        </SideMenu.MenuItem>

        <div className={st.Divider} />

        <SideMenu.MenuItem
          data-testid="events.comparisonFilters"
          leftIcon={<FolderIcon />}
          rightIcon={filterCountComparison > 0 && <Badge value={filterCountComparison} />}
          value={t('labels')}
        >
          <LabelInputPopover
            disabled={!!listId}
            // локальное поле !!!!!!!!!
            // выше в useEffect собирается в top_matching_candidates_label
            fieldName="top_matching_candidates"
            onCountChange={handleCountChange}
          />
          <SimilarityInputsPopover onCountChange={handleCountChange} />
          <TopSimilarObjectIdsInputPopover onCountChange={handleCountChange} />
        </SideMenu.MenuItem>

        <div className={st.Divider} />

        <SideMenu.MenuItem
          data-testid="events.faceAttributesFilters"
          leftIcon={<FolderIcon />}
          rightIcon={filterCountFace > 0 && <Badge value={filterCountFace} />}
          value={t('events:filter form.face attributes and properties')}
        >
          <GenderCheckboxesPopover onCountChange={handleCountChange} />
          <AgeCheckboxesPopover onCountChange={handleCountChange} />
          <EmotionsCheckboxesPopover onCountChange={handleCountChange} />
          <MasksCheckboxesPopover onCountChange={handleCountChange} />
          <LivenessCheckboxesPopover onCountChange={handleCountChange} />
          <DeepfakeCheckboxesPopover onCountChange={handleCountChange} />
          <FaceIdsInputPopover onCountChange={handleCountChange} />
        </SideMenu.MenuItem>

        <div className={st.Divider} />

        <SideMenu.MenuItem
          data-testid="events.bodyAttributesFilters"
          leftIcon={<FolderIcon />}
          rightIcon={filterCountBody > 0 && <Badge value={filterCountBody} />}
          value={t('events:filter form.body attributes and properties')}
        >
          <ApparentAgeCheckboxesPopover onCountChange={handleCountChange} />
          <ApparentGenderCheckboxesPopover onCountChange={handleCountChange} />
          <LowerCheckboxesPopover onCountChange={handleCountChange} />
          <UpperCheckboxesPopover onCountChange={handleCountChange} />
          <HeadwearCheckboxesPopover onCountChange={handleCountChange} />
          <ShoesColorsCheckboxesPopover onCountChange={handleCountChange} />
          <BackpackCheckboxesPopover onCountChange={handleCountChange} />
        </SideMenu.MenuItem>

        <div className={st.Divider} />

        <SideMenu.MenuItem
          data-testid="events.otherFilters"
          leftIcon={<FolderIcon />}
          rightIcon={filterCountOther > 0 && <Badge value={filterCountOther} />}
          value={t('another')}
        >
          <TrackIdsInputPopover onCountChange={handleCountChange} />
          <MetaPopover onCountChange={handleCountChange} />
        </SideMenu.MenuItem>
      </Group>

      <div className={st.ButtonPanel}>
        <Control.Button
          className={st.Button}
          data-testid="FilterForm.clearButton"
          kind="negative"
          onClick={resetForm}
          size={isCompact ? 'small' : 'medium'}
        >
          {t('button.clear')}
        </Control.Button>
      </div>
    </div>
  );
}

FilterForm.propTypes = {
  handlerOptions: PropTypes.arrayOf(optionPropType),
  hasMap: PropTypes.bool,
  setTotalCount: PropTypes.func,
};

export default connect(
  (state) => ({
    handlerOptions: selectHandlerOptions(state),
  }),
  (dispatch) => ({
    setTotalCount: (count) => dispatch(setFilterCounts(count)),
  }),
)(FilterForm);
